import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Search, Plus, X, ShieldCheck, GraduationCap, BookHeart, Building2,
  Eye, EyeOff, CheckCircle2, Circle, Trash2, Layers, Pencil, Copy, RefreshCw, Image as ImageIcon, Upload, AlertCircle,
  UserPlus, UserMinus, Users2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { MODULES } from "@/lib/modules";
import { userCan } from "@/lib/permissions";
import { AVATAR_GROUPS, avatarGroupForAge, avatarGroupsForRole } from "@/lib/avatars";
import { sanitizeName, isValidName, isValidEmail, generateSecurePassword, sanitizePhone, isAcceptedImage, ACCEPTED_IMAGE_MIMES } from "@/lib/validators";
import AvatarUploader from "@/components/AvatarUploader";

const ROLES = [
  { key: "estudiante", label: "Estudiantes", icon: BookHeart, color: "#D63384" },
  { key: "docente", label: "Docentes", icon: GraduationCap, color: "#2C49A0" },
  { key: "franquiciado", label: "Admin Franquicia", icon: Building2, color: "#F5B82E" },
  { key: "admin", label: "Administradores", icon: ShieldCheck, color: "#1F2547" },
];

// Etiqueta del rol según el rol del usuario actual:
//  - Para un Franquiciado, los usuarios de rol "admin" se llaman "Admin Franquicia".
//  - Para super-admin se mantienen las etiquetas globales.
function roleLabel(roleKey, viewerRole) {
  if (viewerRole === "franquiciado" && roleKey === "admin") return "Admin Franquicia";
  return ROLES.find((r) => r.key === roleKey)?.label || roleKey;
}

// Genera una contraseña fuerte aleatoria: 12 caracteres con mayúscula, minúscula,
// dígito y símbolo (cumple la política JASQ®).
function generateStrongPassword(len = 12) {
  const upp = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const low = "abcdefghijkmnpqrstuvwxyz";
  const dig = "23456789";
  const sym = "!@#$%&*?";
  const all = upp + low + dig + sym;
  const r = (s) => s[Math.floor(Math.random() * s.length)];
  const out = [r(upp), r(low), r(dig), r(sym)];
  while (out.length < len) out.push(r(all));
  return out.sort(() => Math.random() - 0.5).join("");
}

const SEDE_PRINCIPAL = "sede-principal";

const ROLE_DEFAULTS = {
  admin: MODULES.map((m) => m.id),
  franquiciado: ["M1", "M10", "M14"],
  docente: ["M2", "M3", "M4", "M5", "M6", "M7", "M8"],
  estudiante: ["M2", "M3", "M4", "M5", "M6"],
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  // v5.6.4 · el admin general (super admin o ligado a la Sede Principal) opera sobre TODAS las franquicias.
  const isGlobalAdmin = isAdmin && (!!me?.is_super_admin || !me?.franchise_id || me?.franchise_id === SEDE_PRINCIPAL);
  const canCreateUsers = userCan(me, "M1", "create");
  const canEditUsers = userCan(me, "M1", "edit");
  const canDeleteUsers = userCan(me, "M1", "delete");

  const [users, setUsers] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [tab, setTab] = useState("estudiante");
  const [q, setQ] = useState("");
  const [franchiseFilter, setFranchiseFilter] = useState("");
  const [sedeFilter, setSedeFilter] = useState("");
  const [classroomFilter, setClassroomFilter] = useState("");
  const [sedes, setSedes] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewingModules, setViewingModules] = useState(null);
  const [assigningTeacher, setAssigningTeacher] = useState(null); // { user } | { bulkIds: [] }
  const [assigningHierarchy, setAssigningHierarchy] = useState(null); // { bulkIds: [] }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { role: tab };
      if (isAdmin && franchiseFilter) params.franchise_id = franchiseFilter;
      if (sedeFilter) params.franchise_id = sedeFilter;
      if (classroomFilter) params.classroom_id = classroomFilter;
      const { data } = await api.get("/users", { params });
      setUsers(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); setSelectedIds(new Set()); /* eslint-disable-next-line */ }, [tab, franchiseFilter, sedeFilter, classroomFilter]);
  useEffect(() => { api.get("/franchises").then((r) => setFranchises(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/hierarchy/franchises").then((r) => setSedes(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = franchiseFilter ? { franchise_id: franchiseFilter } : {};
    api.get("/classrooms", { params }).then((r) => setClassrooms(r.data)).catch(() => setClassrooms([]));
  }, [franchiseFilter]);
  useEffect(() => { api.get("/permission-profiles").then((r) => setProfiles(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/teachers").then((r) => setTeachers(r.data)).catch(() => {});
  }, [franchiseFilter]);

  const teacherById = (tid) => teachers.find((t) => t.id === tid);

  const profileById = (pid) => profiles.find((p) => p.id === pid);
  const franchiseById = (fid) => franchises.find((f) => f.franchise_id === fid);

  const filtered = useMemo(() => users.filter((u) =>
    [u.name, u.email].some((x) => (x || "").toLowerCase().includes(q.toLowerCase()))
  ), [users, q]);

  // Paginación (5 por página). Resetea al cambiar tab/búsqueda/filtro.
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [tab, q, franchiseFilter]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(
    () => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filtered, safePage]
  );

  const onDelete = async (id, name) => {
    if (!confirm(`¿Eliminar a ${name}? Esta acción no se puede deshacer.`)) return;
    try { await api.delete(`/users/${id}`); toast.success("Usuario eliminado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Pestañas visibles:
  //   - super-admin: todas
  //   - franquiciado: estudiantes + docentes + admin (renombrado "Admin Franquicia").
  //     No mostramos "franquiciado" porque el franquiciado solo se vería a sí mismo allí.
  //   - resto: estudiantes + docentes + franquiciados.
  const allowedTabs = ROLES.filter((r) => {
    if (isAdmin) return true;
    if (me?.role === "franquiciado") return r.key !== "franquiciado";
    return r.key !== "admin";
  });

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#D63384]">Gestión M1</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547]">Usuarios y roles</h1>
          <p className="text-sm text-[#6E7591] mt-1">Sede Principal + Franquicias. Edita, elimina y administra accesos.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {tab === "estudiante" && selectedIds.size > 0 && canEditUsers && (
            <button
              data-testid="bulk-assign-teacher-btn"
              onClick={() => setAssigningTeacher({ bulkIds: Array.from(selectedIds) })}
              title={`Asignar un mismo docente a ${selectedIds.size} estudiante(s) seleccionados`}
              className="jasq-btn-blue inline-flex items-center gap-2 text-sm"
            >
              <UserPlus size={14} /> Asignar docente a {selectedIds.size} estudiante(s)
            </button>
          )}
          {(tab === "estudiante" || tab === "docente") && selectedIds.size > 0 && canEditUsers && (
            <button
              data-testid="bulk-assign-hierarchy-btn"
              onClick={() => setAssigningHierarchy({ bulkIds: Array.from(selectedIds) })}
              title={`Asignar Institución/Colegio/Salón a ${selectedIds.size} usuario(s)`}
              className="jasq-btn-primary inline-flex items-center gap-2 text-sm"
            >
              <Layers size={14} /> Asignar jerarquía a {selectedIds.size}
            </button>
          )}
          <div className="flex bg-white p-1 rounded-xl border-2 border-[#E1E5F0]">
            {allowedTabs.map((r) => (
              <button key={r.key} data-testid={`tab-${r.key}`} onClick={() => setTab(r.key)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${tab === r.key ? "bg-[#1F2547] text-white" : "text-[#6E7591] hover:text-[#1F2547]"}`}>
                {roleLabel(r.key, me?.role)}
              </button>
            ))}
          </div>
          {isAdmin && (
            <select
              data-testid="franchise-filter"
              value={franchiseFilter}
              onChange={(e) => { setFranchiseFilter(e.target.value); setClassroomFilter(""); }}
              className="px-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-sm font-bold text-[#1F2547] bg-white"
            >
              <option value="">Todas las franquicias</option>
              {franchises.map((f) => (
                <option key={f.franchise_id} value={f.franchise_id}>
                  {f.is_headquarters ? "★ " : ""}{f.name}
                </option>
              ))}
            </select>
          )}
          {/* v5.6.4 · se quitó el 2.º filtro «Todas las franquicias» (duplicado): el de arriba ya filtra por franquicia. */}
          {tab === "estudiante" && classrooms.length > 0 && (
            <select
              data-testid="classroom-filter"
              value={classroomFilter}
              onChange={(e) => setClassroomFilter(e.target.value)}
              className="px-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-sm font-bold text-[#1F2547] bg-white"
            >
              <option value="">Todos los salones</option>
              {classrooms.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.students_count}/{c.capacity})</option>)}
            </select>
          )}
          <div className="flex items-center gap-2 px-3 py-2 bg-white border-2 border-[#E1E5F0] rounded-xl w-56">
            <Search size={15} className="text-[#6E7591]" />
            <input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…"
              className="bg-transparent outline-none text-sm flex-1 font-medium" />
          </div>
          {(q || franchiseFilter || sedeFilter || classroomFilter) && (
            <button
              data-testid="clear-filters-btn"
              onClick={() => { setQ(""); setFranchiseFilter(""); setSedeFilter(""); setClassroomFilter(""); }}
              title="Limpiar todos los filtros"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 border-[#E1E5F0] text-sm font-bold text-[#A52258] bg-white hover:border-[#D43790] transition-colors"
            >
              <X size={14} /> Limpiar filtros
            </button>
          )}
          <button
            data-testid="open-create-user"
            onClick={() => setShowCreate(true)}
            disabled={!canCreateUsers}
            title={canCreateUsers ? "" : "Tu perfil no permite crear usuarios"}
            className="jasq-btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus size={16} /> Crear usuario
          </button>
        </div>
      </div>

      <div className="jasq-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b-2 border-[#E1E5F0] bg-[#F7F8FA]">
              {(tab === "estudiante" || tab === "docente") && (
                <th className="px-3 py-3 w-12 text-center">
                  <div className="flex flex-col items-center gap-0.5">
                    <input
                      type="checkbox"
                      data-testid="select-all-students"
                      title="Seleccionar todos para asignación masiva"
                      checked={paged.length > 0 && paged.every((u) => selectedIds.has(u.id))}
                      onChange={(e) => {
                        const next = new Set(selectedIds);
                        if (e.target.checked) paged.forEach((u) => next.add(u.id));
                        else paged.forEach((u) => next.delete(u.id));
                        setSelectedIds(next);
                      }}
                    />
                    <span className="text-[9px] uppercase tracking-wider font-extrabold text-[#A43790] leading-none">Asig.</span>
                  </div>
                </th>
              )}
              <th className="text-left text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591] px-5 py-3">Nombre</th>
              <th className="text-left text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591] px-5 py-3">Correo</th>
              <th className="text-left text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591] px-5 py-3">Franquicia</th>
              <th className="text-left text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591] px-5 py-3">Perfil</th>
              {tab === "estudiante" && <th className="text-left text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591] px-5 py-3">Curso</th>}
              {tab === "estudiante" && <th className="text-left text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591] px-5 py-3">Docente</th>}
              {tab === "estudiante" && <th className="text-left text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591] px-5 py-3">Diagnóstico</th>}
              <th className="text-left text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591] px-5 py-3">Estado</th>
              <th className="text-right text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591] px-5 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={10} className="px-5 py-10 text-center text-sm text-[#6E7591]">Cargando…</td></tr>)}
            {!loading && filtered.length === 0 && (<tr><td colSpan={10} className="px-5 py-10 text-center text-sm text-[#6E7591]">Sin usuarios.</td></tr>)}
            {filtered.length > 0 && paged.map((u) => {
              const prof = profileById(u.profile_id);
              const f = franchiseById(u.franchise_id);
              const modules = prof?.modules || ROLE_DEFAULTS[u.role] || [];
              return (
                <tr key={u.id} className="border-b border-[#E1E5F0] last:border-0 hover:bg-[#FAFBFE]" data-testid={`user-row-${u.id}`}>
                  {(tab === "estudiante" || tab === "docente") && (
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        data-testid={`select-${u.id}`}
                        checked={selectedIds.has(u.id)}
                        onChange={(e) => {
                          const next = new Set(selectedIds);
                          if (e.target.checked) next.add(u.id);
                          else next.delete(u.id);
                          setSelectedIds(next);
                        }}
                      />
                    </td>
                  )}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <img src={u.avatar} alt="" className="w-9 h-9 rounded-full bg-white border-2 border-[#E1E5F0] object-cover" />
                      <span className="text-sm font-bold text-[#1F2547]">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-[#4C5478] font-medium">{u.email}</td>
                  <td className="px-5 py-3 text-sm text-[#6E7591]">
                    {f ? (
                      <span className="inline-flex items-center gap-1.5">
                        {f.is_headquarters && <span className="text-[#F5B82E]">★</span>}
                        <span className="font-bold text-[#1F2547]">{f.name}</span>
                      </span>
                    ) : u.role === "admin" ? "Global" : "—"}
                  </td>
                  <td className="px-5 py-3 text-sm">
                    {prof ? (
                      <span className="jasq-pill text-white text-xs" style={{ background: prof.color || "#F5B82E" }}>
                        <Layers size={11} /> {prof.name}
                      </span>
                    ) : (
                      <span className="jasq-pill bg-[#F2F4FA] text-[#6E7591] text-xs">Por defecto</span>
                    )}
                  </td>
                  {tab === "estudiante" && <td className="px-5 py-3 text-sm text-[#6E7591]">{u.grade || "—"}</td>}
                  {tab === "estudiante" && (
                    <td className="px-5 py-3 text-sm">
                      {(() => {
                        const t = teacherById(u.teacher_id);
                        const otherFr = t && (t.franchise_id || "sede-principal") !== (u.franchise_id || "sede-principal"); // v5.6.5
                        return t ? (
                          <span className={`inline-flex items-center gap-1.5 jasq-pill text-xs ${otherFr ? "bg-[#FFF7DA] text-[#7A5B00]" : "bg-[#E8EEFB] text-[#2C49A0]"}`}
                            title={otherFr ? "Este docente pertenece a otra franquicia: reasígnalo" : undefined} data-testid={otherFr ? "teacher-other-franchise" : undefined}>
                            <GraduationCap size={11} /> {t.name}{otherFr ? " ⚠ otra franquicia" : ""}
                          </span>
                        ) : (
                          <span className="jasq-pill bg-[#FFE9F0] text-[#A52258] text-xs">
                            <AlertCircle size={11} /> Sin docente
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  {tab === "estudiante" && (
                    <td className="px-5 py-3 text-sm">
                      <span className="jasq-pill bg-[#F7F8FA] text-[#1F2547] text-xs" data-testid={`diag-${u.id}`}>
                        {u.diagnostic_done ? <CheckCircle2 size={13} className="text-[#3FA84D]" /> : <Circle size={13} className="text-[#6E7591]" />}
                        {u.qualitative_status || "Pendiente"}
                      </span>
                    </td>
                  )}
                  {/* Estado de la cuenta (activo/suspendido) — siempre visible */}
                  <td className="px-5 py-3 text-sm">
                    {(() => {
                      const STATUS_ES = { active: "Activo", suspended: "Suspendido", inactive: "Inactivo", pending: "Pendiente" };
                      const st = u.status || "active";
                      const cls = st === "active"
                        ? "bg-[#E7F4E9] text-[#2A7A36]"
                        : st === "pending"
                          ? "bg-[#FFF7DA] text-[#9C7A18]"
                          : "bg-[#FFE9F0] text-[#A52258]";
                      return (
                        <span
                          data-testid={`user-status-${u.id}`}
                          className={`jasq-pill text-xs font-bold ${cls}`}>
                          {STATUS_ES[st] || st}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-5 py-3">
                    {(() => {
                      const userActive = (u.status || "active") === "active";
                      const tooltipBlocked = "Usuario no activo · solo se puede editar o eliminar";
                      return (
                        <div className="flex items-center gap-1.5 justify-end">
                          {u.role === "estudiante" && (
                            <button
                              onClick={() => setAssigningTeacher({ user: u })}
                              data-testid={`assign-teacher-${u.id}`}
                              title={!userActive ? tooltipBlocked : (canEditUsers ? "Asignar / cambiar docente" : "Tu perfil no permite editar usuarios")}
                              disabled={!canEditUsers || !userActive}
                              className="w-9 h-9 rounded-lg bg-[#E8EEFB] hover:bg-[#2C49A0] hover:text-white flex items-center justify-center text-[#2C49A0] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            ><Users2 size={15} /></button>
                          )}
                          <button
                            onClick={() => setViewingModules({ user: u, modules, profile: prof })}
                            data-testid={`view-modules-${u.id}`}
                            title={!userActive ? tooltipBlocked : `Ver ${modules.length} módulos`}
                            disabled={!userActive}
                            className="w-9 h-9 rounded-lg bg-[#E8EEFB] hover:bg-[#315197] hover:text-white flex items-center justify-center text-[#315197] transition-colors relative disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-[#E8EEFB] disabled:hover:text-[#315197]"
                          >
                            <Layers size={15} />
                            <span className="absolute -top-1 -right-1 text-[9px] font-extrabold bg-[#315197] text-white rounded-full w-4 h-4 flex items-center justify-center">{modules.length}</span>
                          </button>
                          <button
                            onClick={() => setEditing(u)}
                            data-testid={`edit-${u.id}`}
                            title={canEditUsers ? "Editar" : "Tu perfil no permite editar usuarios"}
                            disabled={!canEditUsers}
                            className="w-9 h-9 rounded-lg bg-[#FFF7DA] hover:bg-[#FFCC28] hover:text-[#1F2547] flex items-center justify-center text-[#9C7A18] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          ><Pencil size={15} /></button>
                          <button
                            onClick={() => onDelete(u.id, u.name)}
                            data-testid={`delete-${u.id}`}
                            disabled={!canDeleteUsers || (u.role === "admin" && !u.franchise_id)}
                            title={canDeleteUsers ? "Eliminar" : "Tu perfil no permite eliminar usuarios"}
                            className="w-9 h-9 rounded-lg bg-[#FFE9F0] hover:bg-[#D43790] hover:text-white flex items-center justify-center text-[#A52258] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          ><Trash2 size={15} /></button>
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[#E1E5F0] bg-[#F7F8FA] text-xs text-[#6E7591]" data-testid="users-pagination">
            <span>
              Mostrando <b className="text-[#1F2547]">{(safePage - 1) * PAGE_SIZE + 1}</b>–<b className="text-[#1F2547]">{Math.min(safePage * PAGE_SIZE, filtered.length)}</b> de <b className="text-[#1F2547]">{filtered.length}</b>
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                data-testid="users-page-prev"
                className="px-3 py-1.5 rounded-lg border border-[#E1E5F0] bg-white font-bold text-[#1F2547] disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#F5B82E]"
              >‹ Anterior</button>
              <span className="px-3 py-1.5 font-extrabold text-[#1F2547]" data-testid="users-page-indicator">
                {safePage} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                data-testid="users-page-next"
                className="px-3 py-1.5 rounded-lg border border-[#E1E5F0] bg-white font-bold text-[#1F2547] disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#F5B82E]"
              >Siguiente ›</button>
            </div>
          </div>
        )}
      </div>

      {viewingModules && <UserModulesModal {...viewingModules} onClose={() => setViewingModules(null)} />}

      {assigningTeacher && (
        <AssignTeacherModal
          payload={assigningTeacher}
          teachers={teachers}
          users={users}
          onClose={() => setAssigningTeacher(null)}
          onDone={() => { setAssigningTeacher(null); setSelectedIds(new Set()); load(); }}
        />
      )}

      {assigningHierarchy && (
        <BulkHierarchyModal
          payload={assigningHierarchy}
          users={users}
          franchises={franchises}
          isGlobalAdmin={isGlobalAdmin}
          onClose={() => setAssigningHierarchy(null)}
          onDone={() => { setAssigningHierarchy(null); setSelectedIds(new Set()); load(); }}
        />
      )}

      {showCreate && (
        <UserWizard
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
          franchises={franchises}
          sedes={sedes}
          profiles={profiles}
          teachers={teachers}
          isAdmin={isAdmin}
          isGlobalAdmin={isGlobalAdmin}
          initialRole={tab}
          currentFranchiseId={me?.franchise_id}
          isFranquiciado={me?.role === "franquiciado"}
          viewerRole={me?.role}
        />
      )}
      {editing && (
        <UserEditModal
          user={editing}
          franchises={franchises}
          profiles={profiles}
          isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------- Modal: ver módulos de un usuario ----------------
function UserModulesModal({ user, modules, profile, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="user-modules-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <div className="flex items-center gap-3 min-w-0">
            <img src={user.avatar} alt="" className="w-10 h-10 rounded-full bg-white border-2 border-[#E1E5F0] object-cover" />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#2C49A0]">Módulos de</p>
              <h3 className="font-display text-lg font-extrabold text-[#1F2547] truncate">{user.name}</h3>
            </div>
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]"><X size={20} /></button>
        </div>
        <div className="px-6 py-4 bg-[#F7F8FA] border-b border-[#E1E5F0] text-sm">
          {profile ? (
            <span>Perfil <span className="font-bold" style={{ color: profile.color || "#F5B82E" }}>{profile.name}</span> · {modules.length} módulos</span>
          ) : (
            <span className="text-[#6E7591]">Accesos por defecto del rol <b className="text-[#1F2547]">{user.role}</b> · {modules.length} módulos</span>
          )}
        </div>
        <div className="px-6 py-5 grid sm:grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto">
          {MODULES.filter((m) => modules.includes(m.id)).map((m) => {
            const Icon = m.icon;
            const live = ["M1", "M2", "M3", "M10", "M14"].includes(m.id);
            return (
              <div key={m.id} className={`p-3 rounded-xl border-2 flex items-center gap-3 ${
                live ? "border-[#F5B82E] bg-[#FFF7DA]" : "border-[#E1E5F0] bg-[#F7F8FA] opacity-80"
              }`}>
                <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m.color}1A`, color: m.color }}>
                  <Icon size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#1F2547] truncate">{m.id} · {m.name}</p>
                  <p className="text-[11px] text-[#6E7591] truncate">{live ? "Acceso habilitado" : "Próximamente"}</p>
                </div>
                {live ? (
                  <span className="text-xs font-bold text-[#3FA84D]">✓</span>
                ) : (
                  <span className="text-[10px] font-extrabold uppercase text-[#6E7591] bg-white border border-[#E1E5F0] rounded-md px-1.5 py-0.5">Soon</span>
                )}
              </div>
            );
          })}
          {modules.length === 0 && (
            <p className="text-sm text-[#6E7591] col-span-2 text-center py-8">Este usuario no tiene módulos habilitados.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- WIZARD CREAR ----------------
function UserWizard({ onClose, onSaved, franchises, sedes = [], profiles, teachers = [], isAdmin, isGlobalAdmin, initialRole, isFranquiciado, currentFranchiseId, viewerRole }) {
  const [step, setStep] = useState(1);
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ checking: false, available: null, error: "" });
  const [customRoles, setCustomRoles] = useState([]);
  const [roleLocked, setRoleLocked] = useState(false);

  // Reglas RBAC (#5, #6, #7):
  //  - super_admin (sede principal): crea admin general (sin sede) + admin/docente/estudiante en cualquier sede.
  //  - admin scoped a sede o franquiciado: NO puede crear franquicias ni admin general.
  //    Sólo admin/docente/estudiante, y SIEMPRE dentro de su sede.
  const isSuperAdmin = typeof isGlobalAdmin === "boolean" ? isGlobalAdmin : (isAdmin && !currentFranchiseId); // admin general (v5.6.4)
  const allowedRoles = isSuperAdmin
    ? ["admin", "docente", "estudiante", "franquiciado"]
    : ["admin", "docente", "estudiante"];

  const [reloadingRoles, setReloadingRoles] = useState(false);
  const reloadCustomRoles = (notify = false) => {
    setReloadingRoles(true);
    return api.get("/roles")
      .then((r) => {
        const list = (r.data || []).filter((rr) => !rr.is_system);
        setCustomRoles(list);
        if (notify === true) {
          toast.success(list.length ? `Roles actualizados: ${list.length} personalizado(s)` : "Roles actualizados. No hay roles personalizados todavía (se crean en Accesibilidad · Roles).");
        }
      })
      .catch(() => { if (notify === true) toast.error("No se pudieron recargar los roles. Intenta de nuevo."); })
      .finally(() => setReloadingRoles(false));
  };
  useEffect(() => { reloadCustomRoles(); /* eslint-disable-next-line */ }, []);

  // v5.6.4 · el asistente abre con el rol de la pestaña activa (Admin Franquicia → franquiciado, etc.).
  const startRole = (["admin", "docente", "estudiante", "franquiciado"].includes(initialRole) && allowedRoles.includes(initialRole)) ? initialRole : "estudiante";
  const [form, setForm] = useState({
    role: startRole,
    custom_role_id: "",
    franchise_id: startRole === "franquiciado" ? "" : (isSuperAdmin ? SEDE_PRINCIPAL : (currentFranchiseId || "")),
    first_name: "",
    last_name: "",
    email: "",
    password: generateSecurePassword(8),
    phone: "",
    guardian_name: "",
    guardian_email: "",
    grade: "5° básico",
    age: 10,
    teacher_id: "",
    avatar: null,
    avatar_source: null,
  });
  const [errors, setErrors] = useState({});

  // El wizard ahora tiene 3 pasos (sin perfil): 1=Rol, 2=Datos, 3=Avatar+Confirmación
  const next = () => setStep((s) => {
    if (s === 1) setRoleLocked(true);
    return Math.min(3, s + 1);
  });
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const canStep1 = !!form.role && (
    (form.role === "admin" && isSuperAdmin) ? true :
    form.role === "franquiciado" ? (!!form.franchise_id && form.franchise_id !== SEDE_PRINCIPAL) :
    !!form.franchise_id
  );

  // Validación step 2
  const step2Errors = useMemo(() => {
    const e = {};
    if (!isValidName(form.first_name)) e.first_name = "Solo letras. Mínimo 2 caracteres.";
    if (!isValidName(form.last_name)) e.last_name = "Solo letras. Mínimo 2 caracteres.";
    if (!isValidEmail(form.email)) e.email = "Correo con formato inválido.";
    else if (emailStatus.available === false) e.email = "Este correo ya está registrado.";
    if (!form.phone || form.phone.replace(/\D/g, "").length < 6) e.phone = "Teléfono requerido (mínimo 6 dígitos).";
    return e;
  }, [form, emailStatus]);

  const canStep2 = Object.keys(step2Errors).length === 0 && !emailStatus.checking && emailStatus.available === true;

  // Verifica disponibilidad de correo con debounce
  useEffect(() => {
    if (!isValidEmail(form.email)) {
      setEmailStatus({ checking: false, available: null, error: "" });
      return;
    }
    setEmailStatus({ checking: true, available: null, error: "" });
    const ctrl = setTimeout(async () => {
      try {
        const { data } = await api.get("/users/check-email", { params: { email: form.email } });
        setEmailStatus({ checking: false, available: !!data.available, error: "" });
      } catch (err) {
        setEmailStatus({ checking: false, available: null, error: "No se pudo verificar el correo" });
      }
    }, 400);
    return () => clearTimeout(ctrl);
  }, [form.email]);

  const onAvatarFile = (file) => {
    if (!file) return;
    if (!isAcceptedImage(file)) {
      toast.error("Solo se permiten imágenes (PNG, JPG, WEBP, GIF).");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("La imagen no debe superar 4 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, avatar: reader.result, avatar_source: "upload" }));
    reader.readAsDataURL(file);
  };

  const regenPassword = () => setForm((f) => ({ ...f, password: generateSecurePassword(8) }));
  const copyPassword = async () => {
    try { await navigator.clipboard.writeText(form.password); toast.success("Contraseña copiada"); }
    catch { toast.error("No fue posible copiar"); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        role: form.role,
        franchise_id: form.role === "admin" && isSuperAdmin && !form.franchise_id ? null : form.franchise_id,
        name: `${form.first_name.trim()} ${form.last_name.trim()}`,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: form.phone,
        guardian_name: form.role === "estudiante" && form.guardian_name.trim() ? form.guardian_name.trim() : undefined,
        guardian_email: form.role === "estudiante" && form.guardian_email.trim() ? form.guardian_email.trim().toLowerCase() : undefined,
        grade: form.role === "estudiante" ? form.grade : undefined,
        age: form.role === "estudiante" ? form.age : undefined,
        teacher_id: form.role === "estudiante" && form.teacher_id ? form.teacher_id : undefined,
        custom_role_id: form.custom_role_id || undefined,
        avatar: form.avatar || undefined,
        origin: window.location.origin,
      };
      await api.post("/users", payload);
      toast.success("Usuario creado correctamente");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al crear");
    } finally { setSubmitting(false); }
  };

  const availableCustomRoles = (customRoles || []).filter((cr) => {
    if (cr.base_role !== form.role) return false;
    if (cr.scope === "global") return true;
    return !cr.franchise_id || cr.franchise_id === (form.franchise_id || currentFranchiseId);
  });

  // Cuando no hay match para el rol base actual, agrupamos los roles personalizados
  // existentes por base_role para mostrarlos como hint — así el admin sabe que
  // existen pero están atados a otro rol/sede.
  const customRolesByBase = (customRoles || []).reduce((acc, cr) => {
    acc[cr.base_role] = acc[cr.base_role] || [];
    acc[cr.base_role].push(cr);
    return acc;
  }, {});

  const franchiseOptions = (() => {
    let opts = isSuperAdmin ? franchises : franchises.filter((f) => f.franchise_id === currentFranchiseId);
    // No se permite crear usuarios en franquicias suspendidas.
    opts = opts.filter((f) => (f.status || "active") === "active");
    // Un franquiciado siempre se asocia a una franquicia REAL, no a la sede principal.
    if (form.role === "franquiciado") opts = opts.filter((f) => !f.is_headquarters);
    return opts;
  })();

  // Avatares sugeridos por rol/edad (no sólo estudiante)
  const avatarGroups = avatarGroupsForRole(form.role, form.age);

  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex justify-end" data-testid="user-wizard-modal">
      <div className="jasq-drawer-panel bg-white shadow-2xl w-full max-w-lg h-full overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <div>
            <p className="text-[11px] tracking-wider uppercase font-extrabold text-[#A43790]">Asistente · paso {step}/3</p>
            <h3 className="font-display text-xl font-extrabold text-[#1F2547]">Crear usuario</h3>
          </div>
          <button onClick={onClose} data-testid="wizard-close" className="text-[#6E7591] hover:text-[#1F2547]"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto min-h-0">
          {step === 1 && (
            <>
              <Field label={roleLocked ? "Rol (bloqueado)" : "Selecciona un rol"}>
                {/* Grid unificado: roles del sistema + roles personalizados que el usuario pueda elegir.
                    Al elegir un custom, fijamos form.role = base_role y form.custom_role_id = cr.id. */}
                <div className="grid grid-cols-2 gap-2" data-testid="wizard-role-grid">
                  {/* Roles del sistema */}
                  {allowedRoles.map((r) => {
                    const RoleIcon = ROLES.find((x) => x.key === r)?.icon || ShieldCheck;
                    const color = ROLES.find((x) => x.key === r)?.color;
                    const active = form.role === r && !form.custom_role_id;
                    const disabled = roleLocked && !active;
                    return (
                      <button type="button" key={`sys-${r}`} data-testid={`role-${r}`}
                        onClick={() => !roleLocked && setForm({
                          ...form,
                          role: r,
                          custom_role_id: "",
                          teacher_id: r === "estudiante" ? form.teacher_id : "",
                          franchise_id: r === "franquiciado" && form.franchise_id === SEDE_PRINCIPAL ? "" : form.franchise_id,
                        })}
                        disabled={disabled}
                        title={roleLocked ? "El rol queda bloqueado tras avanzar del paso 1." : ""}
                        className={`text-left p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${active ? "border-[#1F2547] bg-[#1F2547] text-white" : "border-[#E1E5F0] hover:border-[#A43790]"} ${disabled ? "opacity-40 cursor-not-allowed hover:border-[#E1E5F0]" : ""}`}>
                        <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: active ? "#ffffff20" : `${color}22`, color: active ? "white" : color }}>
                          <RoleIcon size={18} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm">{viewerRole === "franquiciado" && r === "admin" ? "Admin Franquicia" : (r.charAt(0).toUpperCase() + r.slice(1))}</p>
                          <p className={`text-[10px] uppercase tracking-wider font-extrabold ${active ? "text-[#FFCC28]" : "text-[#6E7591]"}`}>Sistema</p>
                        </div>
                        {active && roleLocked && (
                          <span className="text-[10px] font-extrabold uppercase tracking-wider bg-[#FFCC28] text-[#1F2547] rounded px-1.5 py-0.5">
                            Bloqueado
                          </span>
                        )}
                      </button>
                    );
                  })}

                  {/* Roles personalizados (cada uno aparece como una opción más). */}
                  {(customRoles || [])
                    .filter((cr) => allowedRoles.includes(cr.base_role))
                    .map((cr) => {
                      const baseColor = ROLES.find((x) => x.key === cr.base_role)?.color || "#A43790";
                      const RoleIcon = ROLES.find((x) => x.key === cr.base_role)?.icon || ShieldCheck;
                      const active = form.custom_role_id === cr.id;
                      const disabled = roleLocked && !active;
                      const frName = cr.franchise_id
                        ? (franchises.find((f) => f.franchise_id === cr.franchise_id)?.name || cr.franchise_id)
                        : "Global";
                      // Auto-seteo de la sede al elegir un rol custom scoped a una franquicia.
                      const targetFranchise = cr.franchise_id || form.franchise_id;
                      return (
                        <button type="button" key={`cr-${cr.id}`} data-testid={`role-custom-${cr.id}`}
                          onClick={() => !roleLocked && setForm({
                            ...form,
                            role: cr.base_role,
                            custom_role_id: cr.id,
                            // Si el custom está scoped a una franquicia, fijamos esa franquicia automáticamente.
                            franchise_id: cr.franchise_id ? cr.franchise_id : (
                              cr.base_role === "franquiciado" && form.franchise_id === SEDE_PRINCIPAL ? "" : form.franchise_id
                            ),
                            teacher_id: cr.base_role === "estudiante" ? form.teacher_id : "",
                          })}
                          disabled={disabled}
                          title={roleLocked ? "El rol queda bloqueado tras avanzar del paso 1." : `Rol personalizado · base ${cr.base_role} · ${frName}`}
                          style={active ? { borderColor: cr.color || baseColor, background: cr.color || baseColor } : {}}
                          className={`text-left p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${active ? "text-white" : "border-[#E1E5F0] hover:border-[#A43790]"} ${disabled ? "opacity-40 cursor-not-allowed hover:border-[#E1E5F0]" : ""}`}>
                          <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: active ? "#ffffff20" : `${(cr.color || baseColor)}22`, color: active ? "white" : (cr.color || baseColor) }}>
                            <RoleIcon size={18} />
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm truncate">{cr.name}</p>
                            <p className={`text-[10px] uppercase tracking-wider font-extrabold truncate ${active ? "text-white/80" : "text-[#6E7591]"}`}>
                              {cr.base_role} · {frName}
                            </p>
                          </div>
                          {active && roleLocked && (
                            <span className="text-[10px] font-extrabold uppercase tracking-wider bg-white/30 text-white rounded px-1.5 py-0.5 shrink-0">
                              Bloqueado
                            </span>
                          )}
                        </button>
                      );
                    })}
                </div>
                <div className="flex items-center justify-between mt-2">
                  {roleLocked ? (
                    <p className="text-[11px] text-[#6E7591] font-bold inline-flex items-center gap-1.5" data-testid="wizard-role-locked">
                      <ShieldCheck size={12} className="text-[#A43790]" /> El rol se bloquea al avanzar del paso 1. Cancela para volver a elegir.
                    </p>
                  ) : (
                    <p className="text-[11px] text-[#6E7591]">
                      Los roles personalizados se crean en <b>Accesibilidad · Roles</b>.
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => reloadCustomRoles(true)}
                    title="Recargar roles personalizados"
                    data-testid="wizard-custom-role-refresh"
                    disabled={roleLocked || reloadingRoles}
                    className="px-2 py-1 rounded-lg border-2 border-[#E1E5F0] hover:border-[#A43790] text-[#1F2547] disabled:opacity-40 inline-flex items-center gap-1 text-[11px] font-bold"
                  >
                    <RefreshCw size={12} className={reloadingRoles ? "animate-spin" : ""} /> {reloadingRoles ? "Actualizando…" : "Recargar"}
                  </button>
                </div>
              </Field>
              {/* Sede / Franquicia: SIEMPRE visible. Sólo se oculta cuando es
                  super_admin creando un admin global (sin sede). */}
              {!(form.role === "admin" && isSuperAdmin && !form.franchise_id) && (
                <Field label={form.role === "franquiciado" ? "Franquicia (sin Sede Principal)" : (isSuperAdmin ? "Franquicia" : "Franquicia")}>
                  <select
                    data-testid="wizard-franchise"
                    value={form.franchise_id}
                    onChange={(e) => setForm({ ...form, franchise_id: e.target.value })}
                    disabled={!isSuperAdmin}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#315197] font-medium bg-white disabled:bg-[#F7F8FA] disabled:text-[#6E7591]"
                  >
                    {(isSuperAdmin || form.role === "franquiciado") && <option value="">Selecciona…</option>}
                    {franchiseOptions.map((f) => (
                      <option key={f.franchise_id} value={f.franchise_id}>
                        {f.is_headquarters ? "★ " : ""}{f.name}
                      </option>
                    ))}
                  </select>
                  {form.role === "franquiciado" && franchiseOptions.length === 0 && (
                    <p className="text-[11px] text-[#A52258] font-bold mt-2 inline-flex items-center gap-1.5">
                      <AlertCircle size={12} /> No hay franquicias disponibles. Crea una en <b>Administración · Franquicias</b>.
                    </p>
                  )}
                  {form.role === "franquiciado" && (
                    <p className="text-[11px] text-[#A43790] font-bold mt-1 inline-flex items-center gap-1.5">
                      <ShieldCheck size={12} /> Un franquiciado siempre se asocia a una franquicia real (no a la sede principal).
                    </p>
                  )}
                  {isSuperAdmin && form.franchise_id === SEDE_PRINCIPAL && form.role !== "franquiciado" && (
                    <p className="text-[11px] text-[#A43790] font-bold mt-2 inline-flex items-center gap-1.5">
                      <ShieldCheck size={12} /> Se creará como personal de la Sede Principal (matriz JASQ®).
                    </p>
                  )}
                </Field>
              )}
              {form.role === "admin" && isSuperAdmin && (
                <label className="flex items-center gap-2 text-xs font-bold text-[#1F2547] cursor-pointer" data-testid="wizard-global-admin">
                  <input type="checkbox"
                    checked={!form.franchise_id}
                    onChange={(e) => setForm({ ...form, franchise_id: e.target.checked ? "" : SEDE_PRINCIPAL })} />
                  Crear como <b>Administrador General</b> (sin sede asociada — sólo desde la sede principal)
                </label>
              )}
              {form.role === "admin" && isFranquiciado && (
                <p className="text-[11px] text-[#A43790] font-bold mt-2 inline-flex items-center gap-1.5">
                  <ShieldCheck size={12} /> Este administrador quedará scoped sólo a tu franquicia (no es super-admin).
                </p>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nombre">
                  <input
                    data-testid="wizard-first-name"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: sanitizeName(e.target.value) })}
                    placeholder="Ej. María"
                    className={`w-full px-4 py-3 rounded-xl border-2 ${step2Errors.first_name && form.first_name ? "border-[#EF4444]" : "border-[#E1E5F0]"} outline-none focus:border-[#315197] font-medium`}
                  />
                  {step2Errors.first_name && form.first_name && (
                    <p className="text-[11px] text-[#EF4444] font-bold mt-1 inline-flex items-center gap-1"><AlertCircle size={11} />{step2Errors.first_name}</p>
                  )}
                </Field>
                <Field label="Apellido">
                  <input
                    data-testid="wizard-last-name"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: sanitizeName(e.target.value) })}
                    placeholder="Ej. González"
                    className={`w-full px-4 py-3 rounded-xl border-2 ${step2Errors.last_name && form.last_name ? "border-[#EF4444]" : "border-[#E1E5F0]"} outline-none focus:border-[#315197] font-medium`}
                  />
                  {step2Errors.last_name && form.last_name && (
                    <p className="text-[11px] text-[#EF4444] font-bold mt-1 inline-flex items-center gap-1"><AlertCircle size={11} />{step2Errors.last_name}</p>
                  )}
                </Field>
              </div>

              <Field label="Correo (único)">
                <div className="relative">
                  <input
                    data-testid="wizard-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="correo@ejemplo.com"
                    autoComplete="off"
                    className={`w-full px-4 py-3 pr-10 rounded-xl border-2 ${emailStatus.available === false || (step2Errors.email && form.email) ? "border-[#EF4444]" : emailStatus.available === true ? "border-[#05A859]" : "border-[#E1E5F0]"} outline-none focus:border-[#315197] font-medium`}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {emailStatus.checking && <RefreshCw size={14} className="animate-spin text-[#6E7591]" />}
                    {!emailStatus.checking && emailStatus.available === true && <CheckCircle2 size={16} className="text-[#05A859]" />}
                    {!emailStatus.checking && emailStatus.available === false && <AlertCircle size={16} className="text-[#EF4444]" />}
                  </div>
                </div>
                {emailStatus.available === false && (
                  <p className="text-[11px] text-[#EF4444] font-bold mt-1 inline-flex items-center gap-1" data-testid="wizard-email-error">
                    <AlertCircle size={11} /> Este correo ya está registrado.
                  </p>
                )}
                {emailStatus.available === true && (
                  <p className="text-[11px] text-[#05A859] font-bold mt-1 inline-flex items-center gap-1">
                    <CheckCircle2 size={11} /> Correo disponible.
                  </p>
                )}
              </Field>

              <Field label="Contraseña (auto-generada, 8 caracteres seguros)">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input data-testid="wizard-password" type={showPwd ? "text" : "password"} value={form.password}
                      readOnly
                      className="w-full px-4 py-3 pr-12 rounded-xl border-2 border-[#E1E5F0] bg-[#F7F8FA] outline-none font-mono text-sm font-bold tracking-wider" />
                    <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E7591]" tabIndex={-1}>
                      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <button type="button" onClick={regenPassword} title="Generar nueva"
                    data-testid="wizard-pwd-regen"
                    className="px-3 rounded-xl border-2 border-[#E1E5F0] hover:border-[#A43790] text-[#1F2547]">
                    <RefreshCw size={16} />
                  </button>
                  <button type="button" onClick={copyPassword} title="Copiar"
                    data-testid="wizard-pwd-copy"
                    className="px-3 rounded-xl border-2 border-[#E1E5F0] hover:border-[#A43790] text-[#1F2547]">
                    <Copy size={16} />
                  </button>
                </div>
                <p className="text-[11px] text-[#6E7591] mt-1">Recuerda comunicar la contraseña al usuario por un canal seguro.</p>
              </Field>

              <Field label="Teléfono">
                <input
                  data-testid="wizard-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: sanitizePhone(e.target.value) })}
                  placeholder="+593 99 999 9999"
                  inputMode="tel"
                  className={`w-full px-4 py-3 rounded-xl border-2 ${step2Errors.phone && form.phone ? "border-[#EF4444]" : "border-[#E1E5F0]"} outline-none focus:border-[#315197] font-medium`}
                />
                {step2Errors.phone && form.phone && (
                  <p className="text-[11px] text-[#EF4444] font-bold mt-1 inline-flex items-center gap-1"><AlertCircle size={11} />{step2Errors.phone}</p>
                )}
              </Field>

              {form.role === "estudiante" && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Padre / tutor (opcional)">
                    <input data-testid="wizard-guardian-name" value={form.guardian_name}
                      onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} placeholder="Nombre del padre o tutor"
                      className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#315197] font-medium" />
                  </Field>
                  <Field label="Correo del tutor (opcional)">
                    <input data-testid="wizard-guardian-email" type="email" value={form.guardian_email}
                      onChange={(e) => setForm({ ...form, guardian_email: e.target.value })} placeholder="tutor@correo.com"
                      className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#315197] font-medium" />
                  </Field>
                </div>
              )}

              {form.role === "estudiante" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Edad">
                      <input data-testid="wizard-age" type="number" min={1} max={100} value={form.age}
                        onChange={(e) => setForm({ ...form, age: parseInt(e.target.value) || 10 })}
                        className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#315197] font-medium" />
                    </Field>
                    <Field label="Curso">
                      <select data-testid="wizard-grade" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#315197] font-medium">
                        {["1° básico","2° básico","3° básico","4° básico","5° básico","6° básico","7° básico","8° básico","1° medio","2° medio","3° medio","4° medio","Universidad"].map((g) => <option key={g}>{g}</option>)}
                      </select>
                    </Field>
                  </div>
                  <Field label="Docente acompañante (opcional)">
                    {(() => {
                      const eligible = (teachers || []).filter((t) =>
                        t.franchise_id === form.franchise_id &&
                        ((t.status || "active") === "active")
                      );
                      const picked = eligible.find((t) => t.id === form.teacher_id);
                      return (
                        <>
                          <select
                            data-testid="wizard-teacher"
                            value={form.teacher_id}
                            onChange={(e) => setForm({ ...form, teacher_id: e.target.value })}
                            disabled={eligible.length === 0}
                            className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#315197] font-medium bg-white disabled:bg-[#F7F8FA] disabled:text-[#6E7591]"
                          >
                            <option value="">— Sin docente asignado por ahora —</option>
                            {eligible.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name} · {t.email}
                                {typeof t.students_count === "number" ? ` · ${t.students_count} alumno(s)` : ""}
                              </option>
                            ))}
                          </select>
                          {eligible.length === 0 ? (
                            <p className="text-[11px] text-[#A52258] mt-1 font-bold inline-flex items-center gap-1">
                              <AlertCircle size={11} /> No hay docentes activos en esta franquicia. Podrás asignar uno después.
                            </p>
                          ) : (
                            <p className="text-[11px] text-[#6E7591] mt-1">Esta vista previa es sólo para ti; no se incluye en el correo de bienvenida del alumno.</p>
                          )}
                          {picked && (
                            <div
                              data-testid="wizard-teacher-preview"
                              className="mt-2 flex items-center gap-3 p-3 rounded-xl border-2 border-[#E1E5F0] bg-gradient-to-br from-[#F7F8FA] to-white"
                            >
                              <img
                                src={picked.avatar}
                                alt={picked.name}
                                className="w-12 h-12 rounded-xl bg-white border-2 border-[#E1E5F0] object-cover shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#2C49A0]">Acompañante asignado</p>
                                <p className="text-sm font-extrabold text-[#1F2547] truncate">{picked.name}</p>
                                <p className="text-[11px] text-[#6E7591] truncate">{picked.email}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591]">Alumnos</p>
                                <p
                                  className="text-2xl font-display font-extrabold text-[#A43790] leading-none tabular-nums"
                                  data-testid="wizard-teacher-students-count"
                                >
                                  {typeof picked.students_count === "number" ? picked.students_count : "—"}
                                </p>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </Field>
                </>
              )}
            </>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <AvatarPicker form={form} setForm={setForm} groups={avatarGroups} onUpload={onAvatarFile} />
              <div className="border-t-2 border-dashed border-[#E1E5F0] pt-4">
                <h4 className="font-display font-extrabold text-[#1F2547]">Confirma los datos</h4>
                <div className="jasq-card p-4 space-y-1.5 text-sm mt-2">
                  <RowKV k="Rol" v={form.custom_role_id ? (customRoles.find((r) => r.id === form.custom_role_id)?.name || form.role) : form.role} />
                  <RowKV k="Franquicia" v={form.role === "admin" && isAdmin ? "— global —" : (franchises.find((f) => f.franchise_id === form.franchise_id)?.name || form.franchise_id)} />
                  <RowKV k="Nombre" v={form.first_name} />
                  <RowKV k="Apellido" v={form.last_name} />
                  <RowKV k="Correo" v={form.email} />
                  <RowKV k="Teléfono" v={form.phone} />
                  <RowKV k="Contraseña inicial" v={<span className="font-mono">{form.password}</span>} />
                  {form.role === "estudiante" && (<><RowKV k="Edad" v={`${form.age} años`} /><RowKV k="Curso" v={form.grade} /><RowKV k="Docente" v={form.teacher_id ? (teachers.find((t) => t.id === form.teacher_id)?.name || form.teacher_id) : "— Sin asignar —"} /></>)}
                </div>
                <p className="text-[11px] text-[#A52258] font-bold mt-2 inline-flex items-center gap-1">
                  <AlertCircle size={11} /> El usuario deberá cambiar esta contraseña en su primer ingreso.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E1E5F0] bg-[#F7F8FA] flex items-center gap-3">
          <button onClick={onClose} className="jasq-btn-ghost">Cancelar</button>
          <div className="flex-1" />
          {step > 1 && <button onClick={prev} className="jasq-btn-ghost" data-testid="wizard-back">Atrás</button>}
          {step < 3 && (
            <button onClick={next} data-testid="wizard-next" className="jasq-btn-blue disabled:opacity-50"
              disabled={(step === 1 && !canStep1) || (step === 2 && !canStep2)}>Siguiente</button>
          )}
          {step === 3 && (
            <button onClick={submit} disabled={submitting} className="jasq-btn-primary" data-testid="wizard-submit">
              {submitting ? "Creando…" : "Crear usuario"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AvatarPicker({ form, setForm, groups, onUpload }) {
  const fileInputRef = useState(null);
  const initials = `${form.first_name[0] || "U"}${form.last_name[0] || ""}`.toUpperCase();
  return (
    <div className="space-y-4">
      <Field label="Foto de perfil">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl bg-[#F2F4FA] flex items-center justify-center overflow-hidden border-2 border-[#E1E5F0]">
            {form.avatar ? (
              <img src={form.avatar} alt="avatar" className="w-full h-full object-cover" />
            ) : (
              <span className="font-display text-2xl font-extrabold text-[#1F2547]">{initials}</span>
            )}
          </div>
          <div className="flex-1 space-y-1.5">
            <label className="jasq-btn-blue inline-flex items-center gap-2 cursor-pointer text-sm">
              <Upload size={14} />
              Subir imagen propia
              <input
                type="file"
                accept={ACCEPTED_IMAGE_MIMES.join(",")}
                onChange={(e) => onUpload(e.target.files?.[0])}
                className="hidden"
                data-testid="wizard-avatar-upload"
              />
            </label>
            <p className="text-[11px] text-[#6E7591]">PNG, JPG, WEBP o GIF. Máx 4 MB.</p>
            {form.avatar && (
              <button type="button" onClick={() => setForm({ ...form, avatar: null, avatar_source: null })}
                className="text-[11px] text-[#A52258] font-bold hover:underline" data-testid="wizard-avatar-clear">
                Quitar foto
              </button>
            )}
          </div>
        </div>
      </Field>

      <Field label="O elige un avatar predeterminado">
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.id}>
              <p className="text-[11px] uppercase tracking-wider font-bold text-[#6E7591] mb-1.5">{g.label}</p>
              <div className="grid grid-cols-4 gap-2">
                {g.items.map((av) => {
                  const active = form.avatar === av.url;
                  return (
                    <button
                      type="button"
                      key={av.id}
                      data-testid={`wizard-avatar-${av.id}`}
                      onClick={() => setForm({ ...form, avatar: av.url, avatar_source: "preset" })}
                      className={`relative aspect-square rounded-xl border-2 overflow-hidden transition-all ${active ? "border-[#A43790] ring-4 ring-[#A43790]/20" : "border-[#E1E5F0] hover:border-[#FFCC28]"}`}
                    >
                      <img src={av.url} alt={av.id} className="w-full h-full object-cover bg-white" />
                      {active && (
                        <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#A43790] text-white flex items-center justify-center">
                          <CheckCircle2 size={12} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Field>
    </div>
  );
}

// ---------------- MODAL EDITAR USUARIO ----------------
function UserEditModal({ user, franchises, profiles, isAdmin, onClose, onSaved }) {
  const pwdTouched = useRef(false);
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    grade: user.grade || "",
    age: user.age || 0,
    password: "",
    profile_id: user.profile_id || "",
    avatar: user.avatar || null,
    franchise_id: user.franchise_id || "",
    status: user.status || "active",
    institution_id: user.institution_id || "",
    school_id: user.school_id || "",
    classroom_id: user.classroom_id || "",
  });
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusReason, setStatusReason] = useState(user.status_reason || "");
  const editAvatarGroups = avatarGroupsForRole(user.role, form.age);
  // Jerarquía dinámica para los selects en cascada (#12: la Franquicia es la raíz)
  const [allInst, setAllInst] = useState([]);
  const [allSchools, setAllSchools] = useState([]);
  const [allClassrooms, setAllClassrooms] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [i, c, r] = await Promise.all([
          api.get("/institutions"),
          api.get("/schools"),
          api.get("/classrooms"),
        ]);
        if (!alive) return;
        setAllInst(i.data || []);
        setAllSchools(c.data || []);
        setAllClassrooms(r.data || []);
      } catch { /* silent: jerarquía vacía */ }
    })();
    return () => { alive = false; };
  }, []);
  // Filtros en cascada (Franquicia → Institución → Colegio → Salón)
  const filtInst = allInst.filter((x) => !form.franchise_id || x.franchise_id === form.franchise_id);
  const filtSchools = allSchools.filter((x) => (!form.franchise_id || x.franchise_id === form.franchise_id) && (!form.institution_id || x.institution_id === form.institution_id));
  const filtClassrooms = allClassrooms.filter((x) =>
    (!form.franchise_id || x.franchise_id === form.franchise_id) &&
    (!form.institution_id || x.institution_id === form.institution_id) &&
    (!form.school_id || x.school_id === form.school_id)
  );

  const availableProfiles = (profiles || []).filter((p) => {
    if (user.role === "admin") return false;
    if (p.scope === "global") return true;
    return p.franchise_id === form.franchise_id;
  });

  const save = async () => {
    setSaving(true);
    try {
      // v5.7.5 · se envía SOLO lo que cambió (antes iba todo siempre: un correo antiguo/atípico o un perfil vacío
      // hacían fallar el guardado de cualquier otro dato, p. ej. al editar al Súper Administrador)
      const payload = {};
      const norm = (v) => (v === undefined || v === null ? "" : String(v).trim());
      if (norm(form.name) !== norm(user.name)) payload.name = form.name.trim();
      if (norm(form.email).toLowerCase() !== norm(user.email).toLowerCase()) payload.email = form.email.trim();
      if (norm(form.phone) !== norm(user.phone)) payload.phone = form.phone || null;
      if ((form.avatar || null) !== (user.avatar || null)) payload.avatar = form.avatar;
      if (user.role !== "admin" && norm(form.profile_id) !== norm(user.profile_id)) payload.profile_id = form.profile_id || null;
      if (form.status !== (user.status || "active") || (form.status !== "active" && norm(statusReason) !== norm(user.status_reason))) {
        payload.status = form.status;
        payload.status_reason = form.status === "active" ? null : (statusReason.trim() || null);
      }
      // v5.7.1 · solo se cambia la contraseña si el administrador la escribió o pulsó «Auto» (el autocompletado del navegador ya no la rellena ni la envía)
      if (form.password && pwdTouched.current) payload.password = form.password;
      if (user.role === "estudiante") {
        if (norm(form.grade) !== norm(user.grade)) payload.grade = form.grade || null;
        if (Number(form.age || 0) !== Number(user.age || 0)) payload.age = form.age || null;
      }
      if (isAdmin && user.role !== "admin" && (form.franchise_id || "") !== (user.franchise_id || "")) {
        payload.franchise_id = form.franchise_id || null;
      }
      // Jerarquía (Franquicia → Institución → Colegio → Salón). Enviar los
      // 3 campos para permitir DESASIGNAR (null) si se vacían.
      if (form.institution_id !== (user.institution_id || "")) payload.institution_id = form.institution_id || null;
      if (form.school_id !== (user.school_id || "")) payload.school_id = form.school_id || null;
      if (form.classroom_id !== (user.classroom_id || "")) payload.classroom_id = form.classroom_id || null;
      if (Object.keys(payload).length === 0) {
        toast.info("No hay cambios para guardar");
        onClose();
        return;
      }
      await api.patch(`/users/${user.id}`, payload);
      toast.success("Usuario actualizado");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex justify-end" data-testid="user-edit-modal">
      <div className="jasq-drawer-panel bg-white shadow-2xl w-full max-w-md h-full overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <div className="flex items-center gap-3 min-w-0">
            <img src={form.avatar || user.avatar} alt="" className="w-10 h-10 rounded-full bg-white border-2 border-[#E1E5F0] object-cover" />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#2C49A0]">Editar</p>
              <h3 className="font-display text-lg font-extrabold text-[#1F2547] truncate">{user.name}</h3>
            </div>
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]" data-testid="uedit-close"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto min-h-0">
          <Field label="Foto de perfil">
            <AvatarUploader value={form.avatar} onChange={(v) => setForm({ ...form, avatar: v })} fallbackName={form.name} testid="uedit-avatar" />
          </Field>
          {/* v5.6.4 · galería de avatares también al EDITAR (según rol y edad) */}
          <Field label="O elige un avatar predeterminado">
            <div className="space-y-3" data-testid="uedit-avatar-gallery">
              {editAvatarGroups.map((g) => (
                <div key={g.id}>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-[#6E7591] mb-1.5">{g.label}</p>
                  <div className="grid grid-cols-5 gap-2">
                    {g.items.map((av) => {
                      const active = form.avatar === av.url;
                      return (
                        <button type="button" key={av.id} data-testid={`uedit-avatar-${av.id}`}
                          onClick={() => setForm({ ...form, avatar: av.url })}
                          className={`relative aspect-square rounded-xl border-2 overflow-hidden transition-all ${active ? "border-[#A43790] ring-4 ring-[#A43790]/20" : "border-[#E1E5F0] hover:border-[#FFCC28]"}`}>
                          <img src={av.url} alt={av.id} className="w-full h-full object-cover bg-white" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Field>
          <Field label="Nombre">
            <input data-testid="uedit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
          </Field>
          <Field label="Correo">
            <input data-testid="uedit-email" type="email" autoComplete="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
          </Field>
          <Field label="Teléfono">
            <input data-testid="uedit-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
          </Field>
          {user.role === "estudiante" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Edad">
                <input data-testid="uedit-age" type="number" min={1} max={100} value={form.age}
                  onChange={(e) => setForm({ ...form, age: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
              </Field>
              <Field label="Curso">
                <select data-testid="uedit-grade" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium">
                  <option value="">—</option>
                  {["1° básico","2° básico","3° básico","4° básico","5° básico","6° básico","7° básico","8° básico","1° medio","2° medio","3° medio","4° medio"].map((g) => <option key={g}>{g}</option>)}
                </select>
              </Field>
            </div>
          )}
          {isAdmin && user.role !== "admin" && (
            <Field label="Franquicia">
              <select data-testid="uedit-franchise" value={form.franchise_id} onChange={(e) => setForm({ ...form, franchise_id: e.target.value, institution_id: "", school_id: "", classroom_id: "" })}
                className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium">
                {franchises
                  .filter((f) => (f.status || "active") === "active" || f.franchise_id === form.franchise_id)
                  .map((f) => (
                    <option key={f.franchise_id} value={f.franchise_id}>
                      {f.is_headquarters ? "★ " : ""}{f.name}
                      {(f.status || "active") !== "active" ? " (Suspendida — no asignable)" : ""}
                    </option>
                  ))}
              </select>
            </Field>
          )}

          {/* Asignación jerárquica (Franquicia → Institución → Colegio → Salón) */}
          {user.role !== "admin" && (
            <div className="rounded-xl border-2 border-dashed border-[#E1E5F0] p-3 space-y-3 bg-[#FAFBFD]" data-testid="uedit-hierarchy-block">
              <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790] flex items-center gap-1">
                <Layers size={12} /> Jerarquía organizativa
              </p>
              <p className="text-[10px] text-[#6E7591]">Raíz: la franquicia seleccionada arriba.</p>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Institución (opcional)">
                  <select data-testid="uedit-institution" value={form.institution_id || ""} onChange={(e) => setForm({ ...form, institution_id: e.target.value, school_id: "", classroom_id: "" })}
                    disabled={!form.franchise_id}
                    className="w-full px-3 py-2 rounded-lg border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] text-sm font-medium disabled:opacity-50">
                    <option value="">— ninguna —</option>
                    {filtInst.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </Field>
                <Field label="Colegio (opcional)">
                  <select data-testid="uedit-school" value={form.school_id || ""} onChange={(e) => setForm({ ...form, school_id: e.target.value, classroom_id: "" })}
                    disabled={!form.franchise_id}
                    className="w-full px-3 py-2 rounded-lg border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] text-sm font-medium disabled:opacity-50">
                    <option value="">— ninguno —</option>
                    {filtSchools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
                <Field label={user.role === "estudiante" ? "Salón (máx 15)" : "Salón (opcional)"}>
                  <select data-testid="uedit-classroom" value={form.classroom_id || ""} onChange={(e) => setForm({ ...form, classroom_id: e.target.value })}
                    disabled={!form.franchise_id}
                    className="w-full px-3 py-2 rounded-lg border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] text-sm font-medium disabled:opacity-50">
                    <option value="">— ninguno —</option>
                    {filtClassrooms.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.students_count ?? 0}/{c.capacity ?? 15})</option>)}
                  </select>
                </Field>
              </div>
              <p className="text-[10px] text-[#6E7591] italic">
                La asignación valida la coherencia en cascada. {user.role === "estudiante" && "Si el salón está lleno, recibirás un aviso."}
              </p>
            </div>
          )}
          {user.role !== "admin" && (
            <Field label="Perfil de permisos">
              <select data-testid="uedit-profile" value={form.profile_id} onChange={(e) => setForm({ ...form, profile_id: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium">
                <option value="">Por defecto del rol</option>
                {availableProfiles.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.modules.length} mods</option>)}
              </select>
            </Field>
          )}
          <Field label="Estado">
            <div className="flex gap-2">
              {["active", "suspended"].map((s) => (
                <button key={s} type="button" data-testid={`uedit-status-${s}`}
                  onClick={() => setForm({ ...form, status: s })}
                  className={`flex-1 px-3 py-2 rounded-xl border-2 text-sm font-bold ${form.status === s ? "border-[#1F2547] bg-[#1F2547] text-white" : "border-[#E1E5F0] hover:border-[#F5B82E]"}`}>
                  {s === "active" ? "Activo" : "Suspendido"}
                </button>
              ))}
            </div>
            {form.status !== "active" && (
              <input data-testid="uedit-status-reason" value={statusReason} maxLength={200}
                onChange={(e) => setStatusReason(e.target.value)}
                placeholder="Motivo de la suspensión (se le muestra al usuario al intentar entrar)"
                className="mt-2 w-full px-3 py-2 rounded-lg border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] text-sm font-medium" />
            )}
          </Field>
          <Field label="Cambiar contraseña (opcional)">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input data-testid="uedit-password" type={showPwd ? "text" : "password"} value={form.password}
                  name="jasq-new-password-field" autoComplete="new-password" data-lpignore="true" data-1p-ignore="true"
                  onKeyDown={() => { pwdTouched.current = true; }} onPaste={() => { pwdTouched.current = true; }}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Dejar vacío para no cambiar"
                  className="w-full px-4 py-3 pr-12 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium" />
                <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E7591]" tabIndex={-1}>
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <button
                type="button"
                data-testid="uedit-regenerate-pwd"
                title="Genera una contraseña fuerte aleatoria"
                onClick={() => {
                  const newPwd = generateStrongPassword();
                  pwdTouched.current = true;
                  setForm({ ...form, password: newPwd });
                  setShowPwd(true);
                  try {
                    navigator.clipboard.writeText(newPwd);
                    toast.success("Contraseña generada y copiada al portapapeles");
                  } catch {
                    toast.success("Contraseña generada");
                  }
                }}
                className="px-3 rounded-xl border-2 border-[#A43790] text-[#A43790] hover:bg-[#A43790] hover:text-white font-bold inline-flex items-center gap-1.5"
              >
                <RefreshCw size={14} /> Auto
              </button>
            </div>
            <p className="text-[11px] text-[#6E7591] mt-1">El botón <b>Auto</b> genera una contraseña fuerte y la copia al portapapeles. El usuario deberá cambiarla en su próximo ingreso.</p>
          </Field>
        </div>

        <div className="px-6 py-4 border-t border-[#E1E5F0] bg-[#F7F8FA] flex items-center gap-3">
          <button onClick={onClose} className="jasq-btn-ghost">Cancelar</button>
          <div className="flex-1" />
          <button onClick={save} disabled={saving} className="jasq-btn-primary" data-testid="uedit-submit">
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-wider uppercase font-extrabold text-[#6E7591]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
function RowKV({ k, v }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-[11px] uppercase tracking-wider font-bold text-[#6E7591] min-w-[110px]">{k}</span>
      <span className="font-bold text-[#1F2547]">{v}</span>
    </div>
  );
}

// =================== Asignar / Cambiar Docente =====================
// Asignación masiva de jerarquía (Institución/Colegio/Salón) a docentes/alumnos (#18)
function BulkHierarchyModal({ payload, users, franchises = [], isGlobalAdmin = false, onClose, onDone }) {
  const targets = users.filter((u) => payload.bulkIds.includes(u.id));
  const franchiseIds = Array.from(new Set(targets.map((u) => u.franchise_id)));
  // v5.6.4 · el admin general puede elegir la FRANQUICIA destino (antes solo veía la de los usuarios,
  // y si era la Sede Principal el menú quedaba vacío). Al elegir otra, se mueve a los usuarios.
  const [destFranchise, setDestFranchise] = useState("");
  const sameFranchise = franchiseIds.length === 1 || (isGlobalAdmin && !!destFranchise);
  const franchiseId = (isGlobalAdmin && destFranchise) ? destFranchise : franchiseIds[0];
  const [institutions, setInstitutions] = useState([]);
  const [schools, setSchools] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [form, setForm] = useState({ institution_id: "", school_id: "", classroom_id: "" });
  const [saving, setSaving] = useState(false);
  const hasStudents = targets.some((u) => u.role === "estudiante");

  useEffect(() => {
    if (!sameFranchise) return;
    setInstitutions([]); setSchools([]); setClassrooms([]);
    (async () => {
      const [i, s, c] = await Promise.all([
        api.get("/institutions", { params: { franchise_id: franchiseId } }),
        api.get("/schools", { params: { franchise_id: franchiseId } }),
        api.get("/classrooms", { params: { franchise_id: franchiseId } }),
      ]);
      setInstitutions(i.data || []); setSchools(s.data || []); setClassrooms(c.data || []);
    })().catch(() => {});
  }, [franchiseId, sameFranchise]);

  const filtSchools = schools.filter((s) => !form.institution_id || s.institution_id === form.institution_id);
  const filtClassrooms = classrooms.filter((c) =>
    (!form.institution_id || c.institution_id === form.institution_id) &&
    (!form.school_id || c.school_id === form.school_id)
  );

  const save = async () => {
    setSaving(true);
    try {
      const body = { user_ids: payload.bulkIds };
      if (isGlobalAdmin && destFranchise) body.franchise_id = destFranchise;
      // Sólo enviamos los campos elegidos (se omite si quedó vacío → no toca ese nivel)
      if (form.institution_id) body.institution_id = form.institution_id;
      if (form.school_id) body.school_id = form.school_id;
      if (form.classroom_id) body.classroom_id = form.classroom_id;
      if (!body.institution_id && !body.school_id && !body.classroom_id && !body.franchise_id) {
        toast.error("Elige una franquicia o al menos un nivel (institución, colegio o salón).");
        setSaving(false); return;
      }
      const { data } = await api.post("/users/bulk-assign-hierarchy", body);
      if (data.errors?.length) {
        toast.warning(`${data.updated} asignado(s) · ${data.errors.length} con problemas: ${data.errors[0]}`);
      } else {
        toast.success(`${data.updated} usuario(s) asignados`);
      }
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#1F2547]/60 backdrop-blur-sm grid place-items-center p-4" data-testid="bulk-hierarchy-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-5 py-3 border-b border-[#E1E5F0] flex items-center justify-between shrink-0">
          <h3 className="font-display text-lg font-extrabold text-[#1F2547]">Asignar jerarquía · {targets.length} usuario(s)</h3>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]" data-testid="bulk-hierarchy-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto flex-1">
          {isGlobalAdmin && (
            <Field label="Franquicia destino">
              <select data-testid="bulk-franchise" value={destFranchise}
                onChange={(e) => setDestFranchise(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm font-medium">
                <option value="">— la actual de cada usuario —</option>
                {franchises.filter((f) => (f.status || "active") === "active").map((f) => (
                  <option key={f.franchise_id} value={f.franchise_id}>{f.is_headquarters ? "★ " : ""}{f.name}</option>
                ))}
              </select>
              {destFranchise && franchiseIds.some((f) => f !== destFranchise) && (
                <p className="text-[11px] text-[#A52258] font-bold mt-1">Los usuarios se moverán a esta franquicia (se limpia su docente anterior y su jerarquía previa).</p>
              )}
            </Field>
          )}
          {!sameFranchise ? (
            <div className="rounded-xl bg-[#FFE9F0] border-2 border-[#D63384] p-4 text-sm text-[#A52258] font-bold" data-testid="bulk-hierarchy-error">
              Los usuarios seleccionados pertenecen a franquicias distintas. {isGlobalAdmin ? "Elige arriba la franquicia destino." : "Selecciona usuarios de UNA sola franquicia para la asignación masiva."}
            </div>
          ) : (
            <>
              {institutions.length === 0 && (
                <p className="text-[11px] rounded-lg bg-[#FFF7DA] border border-[#F5B82E] text-[#7A5B00] px-3 py-2" data-testid="bulk-no-hierarchy">
                  Esta franquicia aún no tiene instituciones, colegios ni salones. Créalos en <b>Administración · Jerarquía</b>, o elige otra franquicia destino.
                </p>
              )}
              <p className="text-xs text-[#6E7591]">
                Se asignarán los niveles elegidos a todos los seleccionados (docentes y/o alumnos) dentro de su franquicia.
                {hasStudents && " Los salones llenos (15) se omitirán con aviso."}
              </p>
              <Field label="Institución (opcional)">
                <select data-testid="bulk-institution" value={form.institution_id} onChange={(e) => setForm({ ...form, institution_id: e.target.value, school_id: "", classroom_id: "" })}
                  className="w-full px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm font-medium">
                  <option value="">— ninguna —</option>
                  {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </Field>
              <Field label="Colegio (opcional)">
                <select data-testid="bulk-school" value={form.school_id} onChange={(e) => setForm({ ...form, school_id: e.target.value, classroom_id: "" })}
                  className="w-full px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm font-medium">
                  <option value="">— ninguno —</option>
                  {filtSchools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Salón (opcional)">
                <select data-testid="bulk-classroom" value={form.classroom_id} onChange={(e) => setForm({ ...form, classroom_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm font-medium">
                  <option value="">— ninguno —</option>
                  {filtClassrooms.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.students_count ?? 0}/{c.capacity ?? 15})</option>)}
                </select>
              </Field>
            </>
          )}
        </div>
        <div className="shrink-0 border-t border-[#E1E5F0] px-5 py-3 flex items-center gap-3">
          <button onClick={onClose} className="jasq-btn-ghost" data-testid="bulk-hierarchy-cancel">Cancelar</button>
          <div className="flex-1" />
          <button onClick={save} disabled={saving || !sameFranchise} className="jasq-btn-primary" data-testid="bulk-hierarchy-save">
            {saving ? "Asignando…" : "Asignar"}
          </button>
        </div>
      </div>
    </div>
  );
}


function AssignTeacherModal({ payload, teachers, users, onClose, onDone }) {
  const isBulk = !!payload.bulkIds;
  const targetStudents = isBulk
    ? users.filter((u) => payload.bulkIds.includes(u.id))
    : [payload.user];
  const franchiseIds = Array.from(new Set(targetStudents.map((s) => s.franchise_id)));
  const sameFranchise = franchiseIds.length === 1;
  const eligibleTeachers = sameFranchise
    ? teachers.filter((t) => t.franchise_id === franchiseIds[0] && (t.status || "active") === "active")
    : [];

  const currentTeacherId = (!isBulk && payload.user.teacher_id) || "";
  const [teacherId, setTeacherId] = useState(currentTeacherId);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      if (isBulk) {
        const { data } = await api.post("/students/assign-teacher-bulk", {
          student_ids: payload.bulkIds,
          teacher_id: teacherId || null,
        });
        toast.success(`Asignados ${data.assigned} estudiante(s)${data.skipped ? ` · ${data.skipped} omitidos` : ""}`);
      } else {
        await api.post(`/students/${payload.user.id}/assign-teacher`, {
          teacher_id: teacherId || null,
        });
        toast.success(teacherId ? "Docente asignado" : "Docente desvinculado");
      }
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al asignar docente");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1F2547]/50 backdrop-blur-sm flex justify-end" data-testid="assign-teacher-modal">
      <div className="jasq-drawer-panel bg-white shadow-2xl w-full max-w-md h-full overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E1E5F0]">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#2C49A0]">Acompañamiento</p>
            <h3 className="font-display text-xl font-extrabold text-[#1F2547]">
              {isBulk ? `Asignar docente a ${targetStudents.length} estudiante(s)` : "Asignar / cambiar docente"}
            </h3>
            {!isBulk && (
              <p className="text-xs text-[#6E7591] mt-1 font-bold">{payload.user.name} · {payload.user.email}</p>
            )}
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {!sameFranchise && (
            <div className="rounded-xl border-2 border-[#D63384] bg-[#FFE9F0] p-3 text-sm text-[#1F2547]">
              <p className="font-bold inline-flex items-center gap-2"><AlertCircle size={16} className="text-[#D63384]" /> Mezcla de franquicias</p>
              <p className="text-xs mt-1">Los estudiantes seleccionados pertenecen a {franchiseIds.length} franquicias distintas. Asigna por separado.</p>
            </div>
          )}

          {sameFranchise && (
            <>
              <Field label="Selecciona el docente acompañante">
                <select
                  data-testid="assign-teacher-select"
                  value={teacherId}
                  onChange={(e) => setTeacherId(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#2C49A0] font-medium text-sm"
                >
                  <option value="">— Sin docente (desvincular) —</option>
                  {eligibleTeachers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} · {t.email}
                    </option>
                  ))}
                </select>
                {eligibleTeachers.length === 0 && (
                  <p className="text-xs text-[#A52258] mt-1 font-bold">⚠️ No hay docentes activos en esta franquicia. Crea uno en la pestaña "Docentes".</p>
                )}
              </Field>

              {!isBulk && payload.user.teacher_id && (
                <div className="text-xs text-[#6E7591]">
                  <b>Actualmente acompañado por:</b>{" "}
                  {teachers.find((t) => t.id === payload.user.teacher_id)?.name || payload.user.teacher_id}
                </div>
              )}

              {isBulk && (
                <div className="rounded-xl bg-[#F7F8FA] p-3 max-h-44 overflow-y-auto text-xs">
                  <p className="font-bold text-[#1F2547] mb-1">Estudiantes:</p>
                  <ul className="space-y-0.5 text-[#4C5478]">
                    {targetStudents.map((s) => <li key={s.id}>• {s.name} ({s.grade || "—"})</li>)}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E1E5F0] bg-[#F7F8FA] flex items-center gap-3">
          <button onClick={onClose} className="jasq-btn-ghost" data-testid="assign-teacher-cancel">Cancelar</button>
          <div className="flex-1" />
          {!isBulk && currentTeacherId && (
            <button
              onClick={() => { setTeacherId(""); }}
              data-testid="assign-teacher-clear"
              className="text-xs font-bold text-[#A52258] hover:underline inline-flex items-center gap-1"
            >
              <UserMinus size={12} /> Desvincular actual
            </button>
          )}
          <button
            onClick={submit}
            disabled={submitting || !sameFranchise}
            data-testid="assign-teacher-submit"
            className="jasq-btn-primary inline-flex items-center gap-2"
          >
            {submitting ? "Guardando…" : <><UserPlus size={14} /> Guardar</>}
          </button>
        </div>
      </div>
    </div>
  );
}

